// Bearer-only client: the reviews endpoints identify the caller by uid, so the
// tickets api key (a CLI convenience) must not be sent from the browser.
import { baseClient } from "./baseRequest";
import {
  ReviewPolicy,
  ReviewPriority,
  ReviewRepo,
  ReviewRequest,
  ReviewRequestStatus,
} from "../views/Tickets/reviews/types";

type ApiReviewRequest = Omit<ReviewRequest, "id"> & { _id: string };

function normalizeReviewRequest(raw: ApiReviewRequest): ReviewRequest {
  const { _id: id, ...fields } = raw;
  return { ...fields, id };
}

export type ReviewSearchInput = {
  statuses?: ReviewRequestStatus[];
  reviewerId?: string;
  requesterId?: string;
  participantId?: string;
  repos?: ReviewRepo[];
  ticketKey?: string;
  priority?: ReviewPriority;
  limit?: number;
  skip?: number;
  sort?: "createdAt" | "-createdAt" | "updatedAt" | "-updatedAt";
};

export type CreateReviewInput = {
  title: string;
  description?: string;
  prUrls: string[];
  reviewerIds: string[];
  policy: ReviewPolicy;
  priority?: ReviewPriority;
  ticketId?: string | null;
};

export type UpdateReviewInput = Partial<CreateReviewInput>;

const reviewsClient = {
  search: async (
    input: ReviewSearchInput,
  ): Promise<{ requests: ReviewRequest[]; total: number }> => {
    const raw = await baseClient.post<{
      requests: ApiReviewRequest[];
      total: number;
    }>({ url: "/admin/reviews/.search", body: input });
    return {
      requests: raw.requests.map(normalizeReviewRequest),
      total: raw.total,
    };
  },

  get: async (id: string): Promise<ReviewRequest> =>
    normalizeReviewRequest(
      await baseClient.get<ApiReviewRequest>({ url: `/admin/reviews/${id}` }),
    ),

  create: async (input: CreateReviewInput): Promise<ReviewRequest> =>
    normalizeReviewRequest(
      await baseClient.post<ApiReviewRequest>({
        url: "/admin/reviews",
        body: input,
      }),
    ),

  update: async (
    id: string,
    input: UpdateReviewInput,
  ): Promise<ReviewRequest> =>
    normalizeReviewRequest(
      await baseClient.put<ApiReviewRequest>({
        url: `/admin/reviews/${id}`,
        body: input,
      }),
    ),

  reRequest: async (id: string): Promise<ReviewRequest> =>
    normalizeReviewRequest(
      await baseClient.post<ApiReviewRequest>({
        url: `/admin/reviews/${id}/re-request`,
      }),
    ),

  remind: async (id: string): Promise<ReviewRequest> =>
    normalizeReviewRequest(
      await baseClient.post<ApiReviewRequest>({
        url: `/admin/reviews/${id}/remind`,
      }),
    ),

  snooze: async (id: string, hours: number): Promise<ReviewRequest> =>
    normalizeReviewRequest(
      await baseClient.post<ApiReviewRequest>({
        url: `/admin/reviews/${id}/snooze`,
        body: { hours },
      }),
    ),

  // `reviewerId` defaults to the caller; only the requester may name another.
  markChecked: async (
    id: string,
    reviewerId?: string,
  ): Promise<ReviewRequest> =>
    normalizeReviewRequest(
      await baseClient.post<ApiReviewRequest>({
        url: `/admin/reviews/${id}/mark-checked`,
        body: { reviewerId },
      }),
    ),

  unmarkChecked: async (
    id: string,
    reviewerId?: string,
  ): Promise<ReviewRequest> =>
    normalizeReviewRequest(
      await baseClient.post<ApiReviewRequest>({
        url: `/admin/reviews/${id}/unmark-checked`,
        body: { reviewerId },
      }),
    ),

  cancel: async (id: string): Promise<ReviewRequest> =>
    normalizeReviewRequest(
      await baseClient.post<ApiReviewRequest>({
        url: `/admin/reviews/${id}/cancel`,
      }),
    ),
};

export default reviewsClient;
